import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { UserDirectoryService } from "@application/useCases/workspace/UserDirectoryService";
import type { UserSummary } from "@domain/schemas/workspace";

const SEARCH_DEBOUNCE_MS = 200;
const PAGE_SIZE = 20;
const POPUP_LIST_HEIGHT_PX = 250;

interface UserPickerProps {
  id?: string;
  /**
  The currently selected user, or `null` when nothing is selected yet. Only `id` is required by
  callers (the backend call needs nothing else), but passing the full {@link UserSummary} when known
  (e.g. resolved for a locked field) avoids an extra round trip to show its display text.
  */
  value: UserSummary | null;
  onChange: (user: UserSummary | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
  User ids to drop from search results - e.g. a team/project's current members, so "Add member"
  can't re-pick someone already on it. Not applied to `value` itself, so a locked/pre-filled field
  still displays correctly even though that user is, by definition, already a member.
  */
  excludeUserIds?: readonly string[];
}

/**
`GET /users?search=` backed combobox - the searchable user field described in
team-project-access-frontend's design.md (WP4.0.1/5.0.1), now that SF-2783 added the endpoint.
Replaces the plain-text user-id `Input` the Assign{Team,Project}RoleDialogs used as an interim
affordance. Fetches the first {@link PAGE_SIZE} candidates immediately on mount - an empty search
term browses the directory instead of matching nothing - so the list isn't empty until the caller
types something; the popup is height-capped so that first page scrolls instead of growing unbounded.
Every keystroke searches (no minimum length), and a footer note appears whenever the directory
holds more matches than the current page, so a short list is never mistaken for the full result set.
*/
export const UserPicker: React.FC<UserPickerProps> = ({ id, value, onChange, disabled, placeholder, excludeUserIds }) => {
  const userDirectoryService = useInjection(UserDirectoryService);
  const [rawResults, setRawResults] = useState<UserSummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceHandle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchRequestId = useRef(0);

  // Pinning/excluding is re-derived from the last fetch rather than baked into state, so a later
  // change to `value`/`excludeUserIds` (e.g. the membership list reloading) is reflected without refetching.
  const data = useMemo(() => {
    const filtered = excludeUserIds ? rawResults.filter((user) => !excludeUserIds.includes(user.id)) : rawResults;
    return value && filtered.every((user) => user.id !== value.id) ? [value, ...filtered] : filtered;
  }, [rawResults, excludeUserIds, value]);

  // The directory can hold far more matches than a single page - tell the user so a short list is
  // never mistaken for "these are all the matches", prompting them to narrow the search instead.
  const hasMoreResults = totalCount > rawResults.length;

  const fetchResults = (searchTerm: string) => {
    setLoading(true);
    const requestId = ++searchRequestId.current;
    void userDirectoryService
      .searchUsersAsync(searchTerm, 1, PAGE_SIZE)
      .then((response) => {
        if (requestId !== searchRequestId.current) return;
        setRawResults(response.list);
        setTotalCount(response.totalCount);
      })
      .finally(() => {
        if (requestId === searchRequestId.current) setLoading(false);
      });
  };

  useEffect(() => {
    fetchResults("");
    return () => {
      if (debounceHandle.current) clearTimeout(debounceHandle.current);
    };
  }, []);

  const handleFilterChange = (event: ComboBoxFilterChangeEvent) => {
    const searchTerm = event.filter.value.trim();
    if (debounceHandle.current) clearTimeout(debounceHandle.current);

    setLoading(true);
    debounceHandle.current = setTimeout(() => fetchResults(searchTerm), SEARCH_DEBOUNCE_MS);
  };

  const handleChange = (event: ComboBoxChangeEvent) => {
    const next = event.value;
    const isUserSummary = Boolean(next) && typeof next === "object" && "id" in next;
    onChange(isUserSummary ? (next as UserSummary) : null);
  };

  return (
    <ComboBox
      id={id}
      style={{ width: "100%" }}
      data={data}
      value={value}
      textField="displayName"
      dataItemKey="id"
      filterable
      loading={loading}
      disabled={disabled}
      placeholder={placeholder ?? "Search by name or username…"}
      popupSettings={{ height: POPUP_LIST_HEIGHT_PX }}
      footer={
        hasMoreResults ? (
          <div style={{ padding: "6px 8px", fontSize: 12, color: "var(--kendo-color-subtle, #6b7280)" }}>
            Showing {rawResults.length} of {totalCount} matches - keep typing to narrow the search.
          </div>
        ) : null
      }
      onFilterChange={handleFilterChange}
      onChange={handleChange}
    />
  );
};
